/* istanbul ignore file */

/*! Please do not import any other dependencies or files has dependencies !*/
import { SettingsIcon, UserIcon } from '@panwds/icons'
import { MSP_MICRO_APP, ASC_TIER_1_SUPPORT_ROLE, MSP_URL, STATE_READY_EVENT_NAME } from './common/constants/InitConstants' // static consts and no imports
import getSwitcherTitle from './tenantAppSwitcher/TsgNavTitle' // no imports in the file so it can be here
import {
  SPARKY_NAV3_ROUTES_EXTPOINT,
  SPARKY_NAV3_LEFT_NAV_EXTPOINT,
  SPARKY_NAV3_BOTTOM_MOST_LEFT_EXTPOINT,
  type LeftNavItem,
  type TopLeftNavItem,
  type SparkyNavRouteConfig,
  type IsAllowed,
} from '@sparky/framework/extpoints'
import type { NavParams, NavItem, MicroAppConfig, StoreState, ExtensionsConfig, ExtensionConfig, ExtensionsExports, SparkyFramework } from '@sparky/framework'
import type { CommonModuleState } from './common/types'
// Only lodash, @panwds/icons, svg, constants, types and framework exports can be imported here!
// Please do not import feature flag or sparky util here, use `sparkyFramework` and `vars` in microApp fn

let fx: SparkyFramework | undefined

// temp solution: should be removed once we no longer need aiops free
const AIOPS_FREE_PREFIX = '/aiops-free'
const notAiopsFree = () => !window.location.pathname.startsWith(AIOPS_FREE_PREFIX)
const commonServicesAvail = {
  avail: ({ access }: any) => notAiopsFree() && !access?.hasRoleOnly?.(ASC_TIER_1_SUPPORT_ROLE),
  availFallbackRedirect: () => (notAiopsFree() ? undefined : AIOPS_FREE_PREFIX),
}

// TODO: update the temp regex once MSP support the new tenant selector in all msp pages
const MSP_PATH_PREFIX_REGEX = /^\/(msp\/manage\/(?:snippets)|msp|asc)?\b.*$/

const getStateReadyPromise = <T>(result: T) => new Promise<T>((resolve, reject) => {
  if (!fx?.store?.stateRef) {
    return reject(new Error('framework is not initialized'))
  }
  const { stateRef } = fx.store
  if (stateRef.current.commonModule?.tsg?.id) {
    return resolve(result)
  }
  const ts = setTimeout(() => {
    if (stateRef.current.commonModule?.tsg?.id) {
      return resolve(result)
    }
    reject(new Error('CommonDrawerState initialization timed out'))
  }, 10000) // 10s
  fx.events.once(STATE_READY_EVENT_NAME, () => {
    clearTimeout(ts)
    resolve(result)
  })
})

const importComponentModule = (name: string) => import('./components').then((module: any) => ({ default: module[name] }))
const importComponent = (name: string) => {
  const fn = () => importComponentModule(name)
  fn.lazy = true
  return fn
}
const importPageComponentWithState = (name: string, extra?: Record<string, string | boolean>) => {
  const fn = () => getStateReadyPromise(importComponentModule(name))
  fn.lazy = true
  if (extra) {
    Object.assign(fn, extra)
  }
  return fn
}

const Subscriptions = importPageComponentWithState('Subscriptions')
const TenantManagement = importPageComponentWithState('TenantManagement')
const DevicesAssociations = importPageComponentWithState('DevicesAssociations')
const IAMTabs = importPageComponentWithState('IAM', { layout: 'tabs' })
const IpAllowList = importPageComponentWithState('IpAllowList')
const UserPreferences = importComponent('UserPreferences')
const SubscriptionFlowCheck = importComponent('SubscriptionFlowCheck')
const Activation = importComponent('Activation')
const Hub = importComponent('Hub')
const ZTPDeviceActivation = importComponent('ZTPDeviceActivation')
const TsgNavIcon = importComponent('TsgNavIcon')
const TenantAppSwitcher = importComponent('TenantAppSwitcher')
const ControlledSwitcherSheet = importComponent('ControlledSwitcherSheet')
const HiddenTenantSwitcher = importComponent('HiddenTenantSwitcher')
const TenantSwitcher = importComponent('TenantSwitcher')
const Dev = () => Promise.resolve({ default: () => null })

type SparkyStoreState = StoreState & { commonModule?: CommonModuleState }

export const modules = {
  Subscriptions,
  TenantManagement,
  DevicesAssociations,
  IAMTabs,
  IpAllowList,
  UserPreferences,
  SubscriptionFlowCheck,
  Activation,
  Hub,
  ZTPDeviceActivation,
  TsgNavIcon,
  TenantAppSwitcher,
  TenantSwitcher,
  Dev,
}

const microApp = ({ vars, sparkyFramework }: NavParams): MicroAppConfig | null => {
  if (!sparkyFramework.hasTsgSupport()) {
    return null
  }

  fx = sparkyFramework
  sparkyFramework.actions.loadApps().catch(() => { })

  const fxVars = sparkyFramework.getFrameworkVars()
  if (fxVars.private_site_default_tsg || fxVars.copilot_csp_mode || fxVars.pdf_mode) {
    return null // disable in GCS, CSP Copilot and PDF Service
  }

  const hubStateProps = ({ auth }: StoreState) => ({ vars, auth })
  const commonModuleStateProps = ({ auth, commonModule, theme }: SparkyStoreState) => ({
    vars,
    fxVars,
    auth,
    commonModule,
    theme,
  })
  const isNetSec = sparkyFramework.isOidcClient(fxVars.netsec_client_id || 'scm')

  const getCommonServicePages = (): NavItem[] => {
    if (!fxVars.use_new_left_nav || fxVars.hub) {
      return []
    }

    const pages: NavItem[] = [
      {
        key: 'subscriptions',
        path: '/settings/subscriptions',
        title: 'Subscriptions',
        description: 'Manage all the licenses, products, and activation profiles that you have access to based on your Customer Service Portal account.',
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: Subscriptions,
        mapStateToProps: commonModuleStateProps,
        ...commonServicesAvail,
        version: 'v2', // for sitemap extraction, do not remove!
      },
      {
        key: 'tenants',
        path: '/settings/tenants',
        title: 'Tenants',
        description: 'Review details about the product licenses and deployment profiles associated with your tenants and subtenants, and define your tenant hierarchy.',
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: TenantManagement,
        mapStateToProps: commonModuleStateProps,
        avail: ({ access, auth }: any) => {
          if (vars.default_tsg) {
            const { tsgs } = auth
            return tsgs?.hasMsp && commonServicesAvail.avail({ access })
          }
          return commonServicesAvail.avail({ access })
        },
        version: 'v2', // for sitemap extraction, do not remove!
      },
      vars.default_tsg && {
        key: 'products',
        path: '/settings/products',
        title: 'Products',
        description: 'Review details about the product licenses and deployment profiles associated with your Customer Service Portal account.',
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: TenantManagement,
        mapStateToProps: commonModuleStateProps,
        avail: ({ access, auth }: any) => {
          const { tsgs } = auth
          return !tsgs?.hasMsp && commonServicesAvail.avail({ access })
        },
        version: 'v2', // for sitemap extraction, do not remove!
      },
      vars.das_enabled && {
        key: 'das',
        path: '/settings/devices',
        title: 'Device Associations',
        description: 'Associate devices with a tenant, associate apps with devices, and remove device associations.',
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: DevicesAssociations,
        mapStateToProps: commonModuleStateProps,
        ...commonServicesAvail,
        version: 'v2', // for sitemap extraction, do not remove!
      },
      {
        key: 'iam',
        path: '/settings/iam',
        title: 'Identity & Access',
        description: 'Identity and access management is role based access control (RBAC) and permissions for authentication and authorization of admin users for all applications and API-based access.',
        expandable: false,
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: IAMTabs,
        mapStateToProps: commonModuleStateProps,
        ...commonServicesAvail,
        version: 'v2', // for sitemap extraction, do not remove!
      },
      vars.ip_allow_list && {
        key: 'ip_restrictions',
        path: '/settings/ip',
        title: 'Trusted IP List',
        description: 'Define a list of trusted IP addresses for restricting access to your applications by specifying IP addresses that are allowed on a per tenant basis.',
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: IpAllowList,
        mapStateToProps: commonModuleStateProps,
        ...commonServicesAvail,
        version: 'v2', // for sitemap extraction, do not remove!
      },
      fxVars.user_preferences_ui && {
        key: 'preference',
        path: '/settings/preferences',
        title: 'User Preferences',
        description: 'Customize the Strata Cloud Manager display settings, or set them to match your system settings.',
        expandable: false,
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: UserPreferences,
        mapStateToProps: ({ theme, preferences }: StoreState) => ({ theme: preferences?.user?.theme || theme }),
        ...commonServicesAvail,
        version: 'v2', // for sitemap extraction, do not remove!
      },
      vars.dev && {
        key: 'dev',
        path: '/dev',
        hidden: true,
        component: Dev,
        mapStateToProps: () => ({}),
      },
    ].filter(Boolean).map(({ description, version, ...page }) => page)

    const settings: NavItem[] = [{
      key: 'settings',
      path: '/settings',
      title: 'Settings',
      icon: SettingsIcon,
      expandable: true,
      mergeNav: true,
      children: pages,
    }]

    if (sparkyFramework.hasMicroApp(MSP_MICRO_APP)) {
      settings.push({
        key: 'msp-settings',
        mergeNav: true,
        title: 'Settings',
        icon: SettingsIcon,
        hidden: true,
        children: pages.map(item => ({ ...item, key: `msp-${item.key}`, path: `/msp${item.path}` }))
      } as NavItem)
    }

    return settings
  }

  return ({
    navigation: [
      ...getCommonServicePages(),
      ...(!isNetSec ? [{
        key: 'subscription',
        title: 'Subscription',
        path: '/setup',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        component: SubscriptionFlowCheck,
        hidden: true,
      }] : []),
      {
        key: 'profile',
        title: 'Profile',
        path: '/activation/profile',
        description: 'Activate security service subscriptions in a tenant service group by associating a Software NGFW Credits deployment profile.',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        component: Activation,
        hidden: true,
        version: 'v2', // for sitemap extraction, do not remove!
      },
      ...(vars.unified_activation ? [{
        key: 'activation',
        title: 'Activation',
        path: '/activation',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        contentClassName: 'panwds-tw3 panwds-unset-reboot',
        mapStateToProps: commonModuleStateProps,
        component: Activation,
        hidden: true,
      }] : []),
      ...(fxVars.hub ? [{ // alias to /setup for hub2
        key: 'hub-subscription',
        title: 'Hub Subscription',
        path: '/hub/setup',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        component: SubscriptionFlowCheck,
        hidden: true,
      }, {
        key: 'hub',
        title: 'Hub',
        path: '/hub',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        contentClassName: 'panwds-tw3',
        mapStateToProps: hubStateProps,
        component: Hub,
        hidden: true,
      }, {
        key: 'products',
        path: '/marketplace',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        contentClassName: 'panwds-tw3',
        mapStateToProps: hubStateProps,
        component: Hub,
        hidden: true,
      }] : []),
      {
        key: 'ztpdeviceactivation',
        title: 'ZTP Device Activation',
        path: '/ztpdeviceactivation',
        description: 'Activate your Zero Trust Provisioning (ZTP) NGFWs and automatically onboard them to your tenant in Strata Cloud Manager.',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        contentClassName: 'panwds-tw3',
        mapStateToProps: commonModuleStateProps,
        component: ZTPDeviceActivation,
        hidden: true,
        version: 'v2', // for sitemap extraction, do not remove!
      },
    ].filter(Boolean) as NavItem[],
    actions: [
      {
        key: 'tenant-switcher',
        bottomMost: true,
        bypassTsgAvail: true,
        title: () => getSwitcherTitle(sparkyFramework.getAuthState()),
        icon: TsgNavIcon,
        mapStateToProps: (state: SparkyStoreState) => ({
          ...commonModuleStateProps(state),
          userThemePreference: state.preferences?.user?.theme || state.theme,
          pathPrefix: window.location.pathname.replace(MSP_PATH_PREFIX_REGEX, '/$1'),
        }),
        navComponent: fxVars.use_new_left_nav && !fxVars.hub ?
          TenantAppSwitcher : TenantSwitcher
      }
    ],
  })
}

export const extensions: ExtensionsExports = ({ sparkyFramework, vars }) => {
  fx = sparkyFramework
  const fxVars = sparkyFramework.getFrameworkVars()
  if (!fxVars.load_nav_extensions || fxVars.private_site_default_tsg || fxVars.copilot_csp_mode || fxVars.pdf_mode) {
    return [] // disable without load_nav_extensions enabled or in GCS, CSP Copilot and PDF Service
  }

  const hubStateProps = ({ auth }: StoreState) => ({ vars, auth })
  const commonModuleStateProps = ({ auth, commonModule, theme }: SparkyStoreState) => ({
    vars,
    fxVars,
    auth,
    commonModule,
    theme,
  })

  const nonHubIsAllowed = () => !fxVars.hub
  const tenantsIsAllowed = () => {
    if (!nonHubIsAllowed()) {
      return false
    }
    const { tsgs, access } = sparkyFramework.getAuthState()
    return tsgs?.hasMsp && commonServicesAvail.avail({ access })
  }
  const productsIsAllowed = () => {
    if (!nonHubIsAllowed()) {
      return false
    }
    const { tsgs, access } = sparkyFramework.getAuthState()
    return !tsgs?.hasMsp && commonServicesAvail.avail({ access })
  }

  const routes = [
    // pages
    {
      id: 'cm.routes.common-services.subscriptions',
      path: '/settings/subscriptions',
      contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
      component: Subscriptions,
      mapStateToProps: commonModuleStateProps,
      isAllowed: nonHubIsAllowed,
    },
    {
      id: 'cm.routes.common-services.tenants',
      path: '/settings/tenants',
      contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
      component: TenantManagement,
      mapStateToProps: commonModuleStateProps,
      isAllowed: tenantsIsAllowed,
    },
    {
      id: 'cm.routes.common-services.products',
      path: '/settings/products',
      contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
      component: TenantManagement,
      mapStateToProps: commonModuleStateProps,
      isAllowed: productsIsAllowed,
    },
    {
      id: 'cm.routes.common-services.devices',
      path: '/settings/devices',
      contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
      component: DevicesAssociations,
      mapStateToProps: commonModuleStateProps,
      isAllowed: nonHubIsAllowed,
    },
    {
      id: 'cm.routes.common-services.iam',
      path: '/settings/iam',
      contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
      component: IAMTabs,
      mapStateToProps: commonModuleStateProps,
      isAllowed: nonHubIsAllowed,
    },
    {
      id: 'cm.routes.common-services.trusted-ips',
      path: '/settings/trusted-ips',
      replacePaths: '/settings/ip',
      contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
      component: IpAllowList,
      mapStateToProps: commonModuleStateProps,
      isAllowed: nonHubIsAllowed,
    },
    {
      id: 'cm.routes.common-services.preferences',
      path: '/settings/preferences',
      contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
      component: UserPreferences,
      mapStateToProps: ({ theme, preferences }: StoreState) => ({ theme: preferences?.user?.theme || theme }),
      isAllowed: nonHubIsAllowed,
    },
    // routes
    {
      id: 'cm.routes.hub.homepage',
      title: 'Hub',
      path: '/hub',
      flags: {
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
      },
      contentClassName: 'panwds-tw3',
      mapStateToProps: hubStateProps,
      component: Hub,
      isAllowed: () => Boolean(fxVars.hub)
    },
    {
      id: 'cm.routes.hub.products',
      path: '/marketplace',
      flags: {
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
      },
      contentClassName: 'panwds-tw3',
      mapStateToProps: hubStateProps,
      component: Hub,
      isAllowed: () => Boolean(fxVars.hub)
    },
    {
      id: 'cm.routes.activation.profile',
      title: 'Profile',
      path: '/activation/profile',
      flags: {
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
      },
      contentClassName: 'panwds-tw3 panwds-unset-reboot',
      component: Activation,
    },
    {
      id: 'cm.routes.activation',
      title: 'Activation',
      path: '/activation',
      flags: {
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
      },
      contentClassName: 'panwds-tw3 panwds-unset-reboot',
      mapStateToProps: commonModuleStateProps,
      component: Activation,
    },
    {
      id: 'cm.routes.ztpdeviceactivation',
      title: 'ZTP Device Activation',
      path: '/ztpdeviceactivation',
      flags: {
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
      },
      contentClassName: 'panwds-tw3',
      mapStateToProps: commonModuleStateProps,
      component: ZTPDeviceActivation,
      description: 'Activate your Zero Trust Provisioning (ZTP) NGFWs and automatically onboard them to your tenant in Strata Cloud Manager.',
      version: 'v3', // for sitemap extraction, do not remove!
    },
    // (fxVars.dev || vars.dev) && {
    //   id: 'cm.routes.common-services.dev',
    //   path: '/dev',
    //   component: Dev,
    //   mapStateToProps: () => ({}),
    // },
  ].filter(Boolean) as (SparkyNavRouteConfig & { id: string, description?: string, version?: string })[]

  const settingsChildren = [
    {
      path: '/settings/iam',
      title: 'Identity & Access Management',
      description: 'Identity and access management is role based access control (RBAC) and permissions for authentication and authorization of admin users for all applications and API-based access.',
      avail: nonHubIsAllowed,
      version: 'v3', // for sitemap extraction, do not remove!
    },
    {
      path: '/settings/products',
      title: 'Products',
      description: 'Review details about the product licenses and deployment profiles associated with your Customer Service Portal account.',
      avail: productsIsAllowed,
      version: 'v3', // for sitemap extraction, do not remove!
    },
    {
      path: '/settings/tenants',
      title: 'Tenants',
      description: 'Review details about the product licenses and deployment profiles associated with your tenants and subtenants, and define your tenant hierarchy.',
      avail: tenantsIsAllowed,
      version: 'v3', // for sitemap extraction, do not remove!
    },
    {
      key: 'subscriptions',
      title: 'SUBSCRIPTIONS',
      children: [ // there is a subscription usage migrated from /monitor
        {
          path: '/settings/subscriptions',
          title: 'Subscriptions List',
          description: 'Manage all the licenses, products, and activation profiles that you have access to based on your Customer Service Portal account.',
          avail: nonHubIsAllowed,
          version: 'v3', // for sitemap extraction, do not remove!
        },
      ]
    },
    {
      path: '/settings/devices',
      title: 'Device Associations',
      avail: nonHubIsAllowed,
      version: 'v3', // for sitemap extraction, do not remove!
    },
    {
      path: '/settings/trusted-ips',
      title: 'Trusted IPs',
      description: 'Define a list of trusted IP addresses for restricting access to your applications by specifying IP addresses that are allowed on a per tenant basis.',
      contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
      avail: nonHubIsAllowed,
      version: 'v3', // for sitemap extraction, do not remove!
    },
    // { // no longer showing up in menu for nav v3
    //   path: '/settings/preferences',
    //   title: 'User Preferences',
    //   description: 'Customize the Strata Cloud Manager display settings, or set them to match your system settings.',
    //   expandable: false,
    //   avail: nonHubIsAllowed,
    // },
  ].filter(Boolean) as LeftNavItem[]

  const settings: TopLeftNavItem[] = [{
    key: 'settings',
    path: '/settings',
    title: 'System Settings',
    expandable: true,
    children: settingsChildren.map(({ description, version, ...child }) => child),
  }]

  const runIsAllowed = (isAllowed: IsAllowed | undefined) => {
    return typeof isAllowed !== 'function' || Boolean(isAllowed())
  }

  type RouteExts = ExtensionConfig<SparkyNavRouteConfig>[]
  const stRoutes: RouteExts = routes.map(({ id, isAllowed, description, version, ...config }) => ({
    id,
    config: {
      ...config,
      isAllowed: () => !fxVars.msp && runIsAllowed(isAllowed),
    },
  }))
  const mtRoutes: RouteExts = routes.map(({ id, path, isAllowed, description, version, ...config }) => ({
    id: `${id}.msp`,
    config: {
      ...config,
      path: `${MSP_URL}${path}`,
      isAllowed: () => fxVars.msp && runIsAllowed(isAllowed),
    },
  }))
  const routesExt = {
    point: SPARKY_NAV3_ROUTES_EXTPOINT,
    configs: stRoutes.concat(mtRoutes)
  } as ExtensionsConfig

  const navExt = {
    point: SPARKY_NAV3_LEFT_NAV_EXTPOINT,
    configs: [{
      id: 'cm.nav.common-services',
      config: {
        navigation: () => settings,
        isAllowed: () => !fxVars.msp,
      }
    }, {
      id: 'cm.nav.msp.common-services',
      config: {
        isAllowed: () => fxVars.msp,
        navigation: () => [{
          key: 'msp-portal',
          path: MSP_URL,
          overrideLeftNav: {
            match: 'path',
            with: 'children'
          },
          children: [{
            key: 'msp-settings',
            path: '/msp/settings',
            title: 'System Settings',
            expandable: true,
            children: settingsChildren.map(({ path, ...item }) => ({
              path: `${MSP_URL}${path}`,
              ...item,
            })),
          }]
        }]
      }
    }]
  } as ExtensionsConfig

  const navBottomMostExt = {
    point: SPARKY_NAV3_BOTTOM_MOST_LEFT_EXTPOINT,
    configs: [{
      id: 'cm.nav.tenant-switcher',
      config: {
        key: 'tenant-switcher',
        isAllowed: () => true,
        flags: {
          bypassTsgAvail: true,
        },
        title: () => {
          const { tsg_id, tsg_name = tsg_id || '' } = sparkyFramework.getAuthState()
          return tsg_name
        },
        titleComponent: () => getSwitcherTitle(sparkyFramework.getAuthState(), true),
        icon: UserIcon, //TsgNavIcon,
        mapStateToProps: (state: SparkyStoreState) => ({
          ...commonModuleStateProps(state),
          userThemePreference: state.preferences?.user?.theme || state.theme,
          pathPrefix: window.location.pathname.replace(MSP_PATH_PREFIX_REGEX, '/$1'),
        }),
        navComponent: !fxVars.hub ? ControlledSwitcherSheet : HiddenTenantSwitcher,
      }
    }]
  } as ExtensionsConfig

  return [routesExt, navExt, navBottomMostExt]
}

export default microApp
